import React, { useState, useEffect } from 'react';
import { formatNumber, formatCurrency } from '../../utils/formatters';
import { useApp } from '../../contexts/AppContext';
import {
  Calendar,
  CalendarDays,
  CalendarRange,
  Carrot,
  MapPin,
  ChevronRight,
  X,
  TrendingUp,
  Package,
  Search,
  Users as UsersIcon,
  Building2,
} from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
  Legend,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TABS = [
  { id: 'quotidien', label: 'Quotidien', icon: Calendar },
  { id: 'mensuel', label: 'Mensuel', icon: CalendarDays },
  { id: 'annuel', label: 'Annuel', icon: CalendarRange },
  { id: 'produits', label: 'Produits', icon: Carrot },
  { id: 'clients', label: 'Clients', icon: UsersIcon },
  { id: 'origines', label: 'Origines', icon: MapPin },
];

const Statistiques = ({ statistiques, statistiquesProduits, statistiquesOrigines }) => {
  const { settings, apiCall, clients } = useApp();
  const [activeTab, setActiveTab] = useState('quotidien');

  // Détail produit
  const [selectedProduit, setSelectedProduit] = useState(null);
  const [produitMensuel, setProduitMensuel] = useState(null);
  const [produitLoading, setProduitLoading] = useState(false);

  // Détail client
  const [statistiquesClients, setStatistiquesClients] = useState(null);
  const [statsClientsLoading, setStatsClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientMensuel, setClientMensuel] = useState(null);
  const [clientLoading, setClientLoading] = useState(false);

  const [annee, setAnnee] = useState(new Date().getFullYear());

  // Recherche produits / clients
  const [searchProduit, setSearchProduit] = useState('');
  const [searchClient, setSearchClient] = useState('');

  const devise = settings?.devise;

  // Charger les stats par client la 1re fois qu'on ouvre l'onglet
  // eslint-disable-next-line
  useEffect(() => {
    if (activeTab === 'clients' && !statistiquesClients && !statsClientsLoading) {
      setStatsClientsLoading(true);
      apiCall(`${API_URL}/api/statistiques-clients`)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => { if (data) setStatistiquesClients(data); })
        .catch((e) => console.error('Erreur stats clients:', e))
        .finally(() => setStatsClientsLoading(false));
    }
  }, [activeTab]);

  const openProduitDetail = async (nomProduit, anneeChoisie = annee) => {
    setSelectedProduit(nomProduit);
    setProduitLoading(true);
    setProduitMensuel(null);
    try {
      const r = await apiCall(
        `${API_URL}/api/statistiques-produit-mensuel/${encodeURIComponent(nomProduit)}?annee=${anneeChoisie}`
      );
      if (r.ok) setProduitMensuel(await r.json());
    } catch (e) {
      console.error('Erreur chargement stats produit mensuel:', e);
    }
    setProduitLoading(false);
  };

  const closeProduitDetail = () => {
    setSelectedProduit(null);
    setProduitMensuel(null);
  };

  const openClientDetail = async (clientId, anneeChoisie = annee) => {
    setSelectedClient(clientId);
    setClientLoading(true);
    setClientMensuel(null);
    try {
      const r = await apiCall(
        `${API_URL}/api/statistiques-client-mensuel/${clientId}?annee=${anneeChoisie}`
      );
      if (r.ok) setClientMensuel(await r.json());
    } catch (e) {
      console.error('Erreur chargement stats client mensuel:', e);
    }
    setClientLoading(false);
  };

  const closeClientDetail = () => {
    setSelectedClient(null);
    setClientMensuel(null);
  };

  const handleAnneeChange = (newAnnee) => {
    setAnnee(newAnnee);
    if (selectedProduit) openProduitDetail(selectedProduit, newAnnee);
    if (selectedClient) openClientDetail(selectedClient, newAnnee);
  };

  // Filtrage produits / clients par recherche
  const filteredProduits = (statistiquesProduits?.stats_par_produit || []).filter((p) =>
    !searchProduit || p.nom_produit.toLowerCase().includes(searchProduit.toLowerCase())
  );
  const filteredClients = (statistiquesClients?.stats_par_client || []).filter((c) =>
    !searchClient || c.nom_entreprise.toLowerCase().includes(searchClient.toLowerCase())
  );

  return (
    <div className="statistiques">
      <div className="section-header">
        <div>
          <h2>Statistiques des Ventes</h2>
          <p className="cpt-subtitle">Pilotage et analyse de votre activité</p>
        </div>
      </div>

      {/* KPI globaux */}
      {statistiques && (
        <div className="stats-kpi-grid">
          <div className="stats-kpi-card">
            <div className="stats-kpi-icon stats-kpi-icon-blue">
              <Package size={20} />
            </div>
            <div>
              <span className="stats-kpi-label">Commandes traitées</span>
              <span className="stats-kpi-value">{statistiques.total_commandes}</span>
            </div>
          </div>
          <div className="stats-kpi-card">
            <div className="stats-kpi-icon stats-kpi-icon-green">
              <TrendingUp size={20} />
            </div>
            <div>
              <span className="stats-kpi-label">Chiffre d'affaires</span>
              <span className="stats-kpi-value">{formatCurrency(parseFloat(statistiques.total_ca), devise)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="stats-tabs" role="tablist">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={activeTab === t.id}
              className={`stats-tab ${activeTab === t.id ? 'stats-tab-active' : ''}`}
              onClick={() => setActiveTab(t.id)}
              data-testid={`stats-tab-${t.id}`}
            >
              <Icon size={16} />
              <span>{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <div className="stats-tab-panel">
        {activeTab === 'quotidien' && statistiques && (
          <>
            <div className="stats-chart-card">
              <h3>Évolution des 10 derniers jours</h3>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <LineChart data={statistiques.stats_par_jour.slice(-10)}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="jour" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip
                      formatter={(value, name) =>
                        name === 'ca' ? [formatCurrency(value, devise), 'CA'] : [value, 'Commandes']
                      }
                      contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="ca" stroke="#10b981" strokeWidth={2} name="CA" />
                    <Line type="monotone" dataKey="commandes" stroke="#3b82f6" strokeWidth={2} name="Commandes" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Commandes</th>
                    <th>Chiffre d'Affaires</th>
                  </tr>
                </thead>
                <tbody>
                  {statistiques.stats_par_jour.slice(-10).map((stat) => (
                    <tr key={stat.date}>
                      <td>{stat.jour}</td>
                      <td>{stat.commandes}</td>
                      <td>{formatCurrency(stat.ca, devise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'mensuel' && statistiques && (
          <>
            <div className="stats-chart-card">
              <h3>Performance des 18 derniers mois</h3>
              <div style={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={statistiques.stats_par_mois}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="nom_mois" stroke="#6b7280" fontSize={12} />
                    <YAxis stroke="#6b7280" fontSize={12} />
                    <Tooltip
                      formatter={(value) => formatCurrency(value, devise)}
                      contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                    />
                    <Bar dataKey="ca" fill="#10b981" radius={[4, 4, 0, 0]} name="CA" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Mois</th>
                    <th>Commandes</th>
                    <th>Chiffre d'Affaires</th>
                  </tr>
                </thead>
                <tbody>
                  {statistiques.stats_par_mois.map((stat) => (
                    <tr key={stat.date}>
                      <td>{stat.nom_mois}</td>
                      <td>{stat.commandes}</td>
                      <td>{formatCurrency(stat.ca, devise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'annuel' && statistiques && (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Année</th>
                  <th>Commandes</th>
                  <th>Chiffre d'Affaires</th>
                </tr>
              </thead>
              <tbody>
                {statistiques.stats_par_annee.map((stat) => (
                  <tr key={stat.annee}>
                    <td>{stat.annee}</td>
                    <td>{stat.commandes}</td>
                    <td>{formatCurrency(stat.ca, devise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'produits' && statistiquesProduits && (
          <>
            <div className="stats-search-row">
              <p className="section-subtitle" style={{ margin: 0 }}>
                Cliquez sur un produit pour voir le détail mois par mois • Période : {statistiquesProduits.periode}
              </p>
              <div className="stats-search-input-wrap">
                <Search size={16} className="stats-search-icon" />
                <input
                  type="text"
                  className="stats-search-input"
                  placeholder="Rechercher un produit…"
                  value={searchProduit}
                  onChange={(e) => setSearchProduit(e.target.value)}
                  data-testid="search-stats-produits"
                />
                {searchProduit && (
                  <button
                    className="stats-search-clear"
                    onClick={() => setSearchProduit('')}
                    aria-label="Effacer la recherche"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="table-container">
              <table className="stats-clickable-table">
                <thead>
                  <tr>
                    <th>Produit</th>
                    <th>Quantité totale</th>
                    <th>Nombre de commandes</th>
                    <th>Chiffre d'affaires</th>
                    <th style={{ width: 40 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProduits.map((produit, index) => (
                    <tr
                      key={index}
                      onClick={() => openProduitDetail(produit.nom_produit)}
                      data-testid={`produit-row-${index}`}
                    >
                      <td>
                        <strong>{produit.nom_produit}</strong>
                      </td>
                      <td>{formatNumber(produit.quantite_totale)}</td>
                      <td>{produit.nombre_commandes}</td>
                      <td>{formatCurrency(produit.chiffre_affaires, devise)}</td>
                      <td style={{ color: '#9ca3af' }}>
                        <ChevronRight size={16} />
                      </td>
                    </tr>
                  ))}
                  {filteredProduits.length === 0 && (
                    <tr>
                      <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                        Aucun produit ne correspond à votre recherche
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'clients' && (
          <>
            {statsClientsLoading && !statistiquesClients ? (
              <div className="loading">Chargement…</div>
            ) : statistiquesClients ? (
              <>
                <div className="stats-search-row">
                  <p className="section-subtitle" style={{ margin: 0 }}>
                    Cliquez sur un client pour voir le détail mois par mois • Période : {statistiquesClients.periode}
                  </p>
                  <div className="stats-search-input-wrap">
                    <Search size={16} className="stats-search-icon" />
                    <input
                      type="text"
                      className="stats-search-input"
                      placeholder="Rechercher un client…"
                      value={searchClient}
                      onChange={(e) => setSearchClient(e.target.value)}
                      data-testid="search-stats-clients"
                    />
                    {searchClient && (
                      <button
                        className="stats-search-clear"
                        onClick={() => setSearchClient('')}
                        aria-label="Effacer la recherche"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="table-container">
                  <table className="stats-clickable-table">
                    <thead>
                      <tr>
                        <th>Client</th>
                        <th>Commandes</th>
                        <th>Quantité totale</th>
                        <th>Chiffre d'affaires</th>
                        <th style={{ width: 40 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClients.map((c, index) => (
                        <tr
                          key={c.client_id}
                          onClick={() => openClientDetail(c.client_id)}
                          data-testid={`client-row-${index}`}
                        >
                          <td>
                            <strong>{c.nom_entreprise}</strong>
                          </td>
                          <td>{c.nombre_commandes}</td>
                          <td>{formatNumber(c.quantite_totale)}</td>
                          <td>{formatCurrency(c.chiffre_affaires, devise)}</td>
                          <td style={{ color: '#9ca3af' }}>
                            <ChevronRight size={16} />
                          </td>
                        </tr>
                      ))}
                      {filteredClients.length === 0 && (
                        <tr>
                          <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                            Aucun client ne correspond à votre recherche
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <div className="loading">Chargement…</div>
            )}
          </>
        )}

        {activeTab === 'origines' && statistiquesOrigines && (
          <>
            <p className="section-subtitle" style={{ marginBottom: '1rem' }}>
              Période : {statistiquesOrigines.periode} • {statistiquesOrigines.nombre_origines} origine(s)
            </p>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Origine</th>
                    <th>Nombre de produits vendus</th>
                    <th>Chiffre d'affaires</th>
                  </tr>
                </thead>
                <tbody>
                  {statistiquesOrigines.stats_par_origine.map((origine, index) => (
                    <tr key={index}>
                      <td>
                        <strong>{origine.origine}</strong>
                      </td>
                      <td>{origine.nombre_produits}</td>
                      <td>{formatCurrency(origine.chiffre_affaires, devise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Modal détail produit (graphe annuel mois par mois) */}
      {selectedProduit && (
        <div className="modal-overlay" onClick={closeProduitDetail}>
          <div
            className="modal modal-large"
            onClick={(e) => e.stopPropagation()}
            data-testid="produit-detail-modal"
          >
            <div className="modal-header">
              <h3>
                <Carrot size={18} /> {selectedProduit}
              </h3>
              <button className="modal-close" onClick={closeProduitDetail}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '1rem 1.5rem 0' }}>
              <div className="stats-year-selector">
                <label>Année :</label>
                <select
                  value={annee}
                  onChange={(e) => handleAnneeChange(parseInt(e.target.value))}
                  data-testid="produit-detail-annee"
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const y = new Date().getFullYear() - i;
                    return (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {produitLoading && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Chargement…</div>
            )}

            {produitMensuel && !produitLoading && (
              <>
                <div className="stats-kpi-row" style={{ padding: '1rem 1.5rem' }}>
                  <div className="stats-kpi-mini">
                    <span className="stats-kpi-label">Quantité totale</span>
                    <span className="stats-kpi-value">{formatNumber(produitMensuel.total_quantite)}</span>
                  </div>
                  <div className="stats-kpi-mini">
                    <span className="stats-kpi-label">Commandes</span>
                    <span className="stats-kpi-value">{produitMensuel.total_commandes}</span>
                  </div>
                  <div className="stats-kpi-mini">
                    <span className="stats-kpi-label">CA total</span>
                    <span className="stats-kpi-value">{formatCurrency(produitMensuel.total_ca, devise)}</span>
                  </div>
                </div>

                <div style={{ width: '100%', height: 320, padding: '0 1rem' }}>
                  <ResponsiveContainer>
                    <BarChart data={produitMensuel.stats_par_mois}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="nom_mois" stroke="#6b7280" fontSize={12} />
                      <YAxis stroke="#6b7280" fontSize={12} />
                      <Tooltip
                        formatter={(value, name) =>
                          name === 'ca' ? [formatCurrency(value, devise), 'CA'] : [value, name]
                        }
                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                      />
                      <Legend />
                      <Bar dataKey="ca" fill="#10b981" radius={[4, 4, 0, 0]} name="CA" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="table-container" style={{ margin: '1rem 1.5rem' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Mois</th>
                        <th>Quantité</th>
                        <th>Commandes</th>
                        <th>Chiffre d'affaires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {produitMensuel.stats_par_mois.map((m) => (
                        <tr key={m.mois}>
                          <td>{m.nom_mois}</td>
                          <td>{formatNumber(m.quantite)}</td>
                          <td>{m.commandes}</td>
                          <td>{formatCurrency(m.ca, devise)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="form-actions">
              <button onClick={closeProduitDetail} className="btn btn-secondary">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal détail client (graphe annuel mois par mois + top produits) */}
      {selectedClient && (
        <div className="modal-overlay" onClick={closeClientDetail}>
          <div
            className="modal modal-large"
            onClick={(e) => e.stopPropagation()}
            data-testid="client-detail-modal"
          >
            <div className="modal-header">
              <h3>
                <Building2 size={18} /> {clientMensuel?.nom_entreprise || ''}
              </h3>
              <button className="modal-close" onClick={closeClientDetail}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: '1rem 1.5rem 0' }}>
              <div className="stats-year-selector">
                <label>Année :</label>
                <select
                  value={annee}
                  onChange={(e) => handleAnneeChange(parseInt(e.target.value))}
                  data-testid="client-detail-annee"
                >
                  {Array.from({ length: 5 }, (_, i) => {
                    const y = new Date().getFullYear() - i;
                    return (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            {clientLoading && (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Chargement…</div>
            )}

            {clientMensuel && !clientLoading && (
              <>
                <div className="stats-kpi-row" style={{ padding: '1rem 1.5rem' }}>
                  <div className="stats-kpi-mini">
                    <span className="stats-kpi-label">Commandes</span>
                    <span className="stats-kpi-value">{clientMensuel.total_commandes}</span>
                  </div>
                  <div className="stats-kpi-mini">
                    <span className="stats-kpi-label">Quantité totale</span>
                    <span className="stats-kpi-value">{formatNumber(clientMensuel.total_quantite)}</span>
                  </div>
                  <div className="stats-kpi-mini">
                    <span className="stats-kpi-label">CA total</span>
                    <span className="stats-kpi-value">{formatCurrency(clientMensuel.total_ca, devise)}</span>
                  </div>
                </div>

                <div style={{ width: '100%', height: 320, padding: '0 1rem' }}>
                  <ResponsiveContainer>
                    <BarChart data={clientMensuel.stats_par_mois}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="nom_mois" stroke="#6b7280" fontSize={12} />
                      <YAxis stroke="#6b7280" fontSize={12} />
                      <Tooltip
                        formatter={(value, name) =>
                          name === 'ca' ? [formatCurrency(value, devise), 'CA'] : [value, name]
                        }
                        contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
                      />
                      <Legend />
                      <Bar dataKey="ca" fill="#10b981" radius={[4, 4, 0, 0]} name="CA" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Top produits du client */}
                {clientMensuel.top_produits && clientMensuel.top_produits.length > 0 && (
                  <div style={{ padding: '0 1.5rem', marginTop: '1rem' }}>
                    <h4 style={{ margin: '0 0 0.5rem', fontSize: '0.95rem', color: '#374151' }}>
                      Produits achetés cette année
                    </h4>
                    <div className="table-container">
                      <table>
                        <thead>
                          <tr>
                            <th>Produit</th>
                            <th>Quantité</th>
                            <th>Chiffre d'affaires</th>
                          </tr>
                        </thead>
                        <tbody>
                          {clientMensuel.top_produits.map((p, i) => (
                            <tr key={i}>
                              <td>{p.nom_produit}</td>
                              <td>{formatNumber(p.quantite)}</td>
                              <td>{formatCurrency(p.ca, devise)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="table-container" style={{ margin: '1rem 1.5rem' }}>
                  <table>
                    <thead>
                      <tr>
                        <th>Mois</th>
                        <th>Commandes</th>
                        <th>Quantité</th>
                        <th>Chiffre d'affaires</th>
                      </tr>
                    </thead>
                    <tbody>
                      {clientMensuel.stats_par_mois.map((m) => (
                        <tr key={m.mois}>
                          <td>{m.nom_mois}</td>
                          <td>{m.commandes}</td>
                          <td>{formatNumber(m.quantite)}</td>
                          <td>{formatCurrency(m.ca, devise)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="form-actions">
              <button onClick={closeClientDetail} className="btn btn-secondary">
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Statistiques;
